// Magviz entry point. Three.js + jsfive HDF5 viewer.
//
// This file's job: build the viewer context, install the small bits that
// can't live in createViewerContext (closures over the THREE stack the
// bootstrap will build), then hand off to bootstrap.
//
// Logic lives in carved modules:
//   - src/app/bootstrap.ts         conductor; phases under src/app/bootstrap/
//   - src/app/intents/             per-domain intent handlers (index.ts wires)
//   - src/app/datasetManager.ts    switchDataset, switchFieldComponent
//   - src/app/sceneIO.ts           scene.json fetch + parse + object spawn
//   - src/app/colormapSync.ts      cross-driver colormap fan-out
//   - src/app/fieldData.ts         HDF5 loader (wraps @physics/grid with intents)
//   - src/app/theme.ts             theme loader; theme apply via UI_THEME_APPLY
//   - src/app/devtools.ts          window.__magviz handle
//   - src/adapters/{coordinates,fieldlines,debug}.ts  THREE-wrappers + util
//   - src/physics/{probe,colormap,trace,sample,grid,contour,coords}.ts  pure math
//   - src/scene/{slices,volume,contour,grid,axes,selection,fieldlines,picking,regionContext}.ts
//   - src/store/{instance,proxy,store,intents,schema,defaults}.ts
//   - src/ui/{panels,events,coordinateDisplay,colorbar,…}.ts + src/ui/panel/**
//   - src/input/{keyboard,shortcuts}.ts
//   - src/render/{loop,lighting,screenshot}.ts
//   - src/shaders/*.glsl  (Vite ?raw imports)

import { createViewerContext } from './context';
import { mapPhysicalToThreeWithRotation } from '@adapters/coordinates';
import { createObjectRegistry } from './objectRegistry';
import { CAMERA_DEFAULTS } from '@config/defaults';
import { store, params } from '@store/instance';
import { bootstrap } from './bootstrap';
import { installDevtools } from './devtools';
import { installLoadingDriver } from '@ui/loading';

const viewer = createViewerContext();
viewer.objects.sceneRegistry = createObjectRegistry({ mapPhysicalToThreeWithRotation }) as unknown as Record<string, unknown>;

// Reset-camera button closes over the THREE stack that bootstrap builds.
(params as unknown as { resetView: () => void }).resetView = (): void => {
    const camera = viewer.three.camera;
    const controls = viewer.three.controls;
    if (camera) {
        camera.position.set(CAMERA_DEFAULTS.position.x, CAMERA_DEFAULTS.position.y, CAMERA_DEFAULTS.position.z);
        // Restore default framing for both projections (zoom is the ortho lever;
        // harmless to reset on perspective, where it's normally 1).
        camera.zoom = 1;
        camera.updateProjectionMatrix();
    }
    if (controls) {
        controls.target.set(CAMERA_DEFAULTS.target.x, CAMERA_DEFAULTS.target.y, CAMERA_DEFAULTS.target.z);
        controls.update();
    }
};

installDevtools(store, viewer);

// Wire the loading overlay listener BEFORE bootstrap dispatches its first
// UI_LOADING_SHOW. The store silently drops intents with no handler, so
// installing this inside wireUI (which runs after the field-load block)
// would lose every show/hide/progress emitted during cold start.
installLoadingDriver(store);

window.addEventListener('load', () => { bootstrap(viewer); });
